<script>
  import { onMount } from 'svelte'
  import { listGiftCards } from '../../models/giftCard.js'
  import { groupGiftCards, isPlainAmount } from '../../models/giftCardGroups.js'
  import { cart } from '../../models/cart.js'
  import { formatPrice } from '../../services/utilities.js'
  import GiftCardBuyButton from '../../components/GiftCardBuyButton.svelte'
  import Alert from '../../components/Alert.svelte'
  import GiftPageHeader from '../../components/GiftPageHeader.svelte'

  const meta = {
    title: 'Gift Cards | Missing Piece Puzzle Exchange',
    description:
      'Give the gift of puzzles. Purchase a Missing Piece Puzzle Exchange gift card for any puzzle lover.',
    canonical: 'https://www.puzzleexchange.com/gift-cards',
  }

  let loading = true
  let error = null
  let groups = { plans: [], packs: [], other: [] }

  // keeps the "go to checkout" bar in view once a gift card is in the cart
  $: giftCardsInCart = $cart
    .filter((p) => p.product_type && p.product_type.id === 6)
    .reduce((a, p) => a + p.quantity, 0)

  onMount(async () => {
    let products = null
    try {
      products = await listGiftCards()
    } catch (e) {}
    if (!products || products.error) {
      error =
        "We couldn't load gift cards right now. Please refresh the page, or email info@puzzleexchange.com and we will help."
    } else {
      groups = groupGiftCards(products)
    }
    loading = false
  })
</script>

<svelte:head>
  <title>{meta.title}</title>
  <meta name="description" content={meta.description} />
  <meta property="og:title" content={meta.title} />
  <meta property="og:description" content={meta.description} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content={meta.canonical} />
  <link rel="canonical" href={meta.canonical} />
</svelte:head>

<style type="text/less">
  // Uses the site's colors, type and rounded cards (and the shared navy gift
  // header), but every option is drawn as a gift card (pale blue with a navy
  // label stripe) so it can't be mistaken for the membership cards on Ways to
  // Rent.

  .gift-cards-page {
    font-size: 1.125rem;
    line-height: 1.6;
    color: var(--col-gray-10);

    p {
      margin: 0 0 var(--spacing-sm);
    }

    &.has-bar {
      padding-bottom: 7rem;
    }
  }

  .how {
    display: flex;
    justify-content: center;
    gap: var(--spacing-lg);
    flex-wrap: wrap;
    padding: 0;
    margin: 0 auto;
    list-style: none;
    text-align: left;

    li {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-weight: 600;
    }
  }

  .how-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: var(--col-accent-1);
    color: var(--col-accent-2-d);
    font-weight: 700;
  }

  .content {
    max-width: 1040px;
    margin: 0 auto;
    padding: 0 var(--spacing);
  }

  .section {
    padding: calc(var(--spacing) * 2) 0 var(--spacing);
    text-align: center;

    h2 {
      font-size: 32px;
      font-weight: 400;
      line-height: 38px;
      margin: 0 0 var(--spacing-xs);
    }

    > p {
      max-width: 640px;
      margin: 0 auto;
      color: var(--col-gray-30);
    }
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(270px, 320px));
    justify-content: center;
    gap: var(--spacing-lg);
    margin-top: calc(var(--spacing) * 1.5);
    text-align: left;
  }

  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    background: var(--col-gray-100);
    border: 1px solid var(--col-gray-90);
    border-radius: 16px;
    padding: var(--spacing);

    &.popular {
      border: 2px solid var(--col-accent-2);
    }

    h3 {
      margin: 0 0 var(--spacing-xs);
      font-size: 1.4rem;
      font-weight: 600;
    }

    // keeps buttons lined up when card details differ in length
    > .tile {
      margin-top: auto;
    }

  }

  .badge {
    position: absolute;
    top: calc(var(--spacing-sm) * -1);
    right: var(--spacing);
    background: var(--col-accent-2);
    color: var(--col-gray-100);
    font-size: 0.85rem;
    font-weight: 700;
    padding: 0.1rem var(--spacing-sm);
    border-radius: 999px;
  }

  .steps {
    list-style: none;
    padding: 0;
    margin: 0 0 var(--spacing-sm);
    font-size: 1rem;
    color: var(--col-gray-30);

    li {
      display: flex;
      align-items: flex-start;
      gap: var(--spacing-xs);
      margin-bottom: var(--spacing-xs);
    }

    .icon {
      flex: 0 0 auto;
      width: 1.1rem;
      height: 1.1rem;
      margin-top: 0.25rem;
      color: var(--col-accent-2);
    }

    .highlight {
      font-weight: 700;
      color: var(--col-gray-10);
    }
  }

  .options {
    display: flex;
    flex-direction: column;
    gap: var(--spacing);
  }

  // a gift card: navy stripe on top, then the amount on pale blue.
  // Kept light so the navy Add to Cart button below it stands out.
  .tile {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    background: var(--col-accent-1-xl);
    border: 1px solid var(--col-accent-1);
    color: var(--col-accent-2);
    margin-bottom: var(--spacing-sm);
    box-shadow: 0 6px 16px rgba(0, 51, 102, 0.12);

    .tile-top {
      display: block;
      padding: var(--spacing-sm) var(--spacing-sm) 0;
      font-size: 0.8rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .tile-amount {
      display: block;
      padding: 0 var(--spacing-sm) var(--spacing-xs);
      font-size: 2.25rem;
      font-weight: 700;
      line-height: 1.2;
    }

    .tile-stripe {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-xs);
      background: var(--col-accent-2);
      color: var(--col-gray-100);
      padding: 0.35rem var(--spacing-sm);
      font-weight: 700;
      font-size: 1.05rem;

      .icon {
        flex: 0 0 auto;
        width: 1.3rem;
        height: 1.3rem;
        color: var(--col-accent-1);
      }
    }

    .tile-amount {
      padding-bottom: var(--spacing-sm);
    }
  }

  .loading {
    text-align: center;
    color: var(--col-gray-30);
    padding: var(--spacing-lg) 0;
  }

  .help {
    text-align: center;
    padding: var(--spacing-lg) 0;
  }

  .checkout-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 150;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-sm) var(--spacing);
    padding: var(--spacing-sm) var(--spacing);
    background: var(--col-gray-100);
    border-top: 2px solid var(--col-accent-2);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    font-size: 1.125rem;
    font-weight: 600;

    .btn {
      margin: 0;
      min-height: 3rem;
      padding: 0 var(--spacing-lg);
      display: inline-flex;
      align-items: center;
      font-size: 1.125rem;
    }
  }

  @media (max-width: 600px) {
    .gift-cards-page.has-bar {
      padding-bottom: 9rem;
    }

    .content {
      padding: 0 var(--spacing-sm);
    }

    .cards {
      grid-template-columns: minmax(0, 1fr);
    }

    .card {
      padding: var(--spacing) var(--spacing-sm);
    }

    .how {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-sm);
      max-width: 320px;
    }

    .checkout-bar .btn {
      box-sizing: border-box;
      width: 100%;
      padding: 0 var(--spacing-sm);
      justify-content: center;
    }
  }
</style>

<div class="gift-cards-page" class:has-bar={giftCardsInCart > 0}>
  <GiftPageHeader
    title="Gift Cards"
    intro="Give the gift of puzzling! Every gift card is a one-time amount. We'll email it to you right after purchase so you can pass it along.">
    <ol class="how">
      <li><span class="how-num">1</span> Choose a gift card</li>
      <li><span class="how-num">2</span> We email it to you</li>
      <li>
        <span class="how-num">3</span>
        They redeem it at puzzleexchange.com/redeem
      </li>
    </ol>
  </GiftPageHeader>

  <div class="content">
    {#if loading}
      <p class="loading">
        <svg class="icon spin"><use href="/icons.svg#circle_notch" /></svg>
        Loading gift cards...
      </p>
    {:else if error}
      <div class="section"><Alert type="danger" icon="warning">{error}</Alert></div>
    {:else}
      {#if groups.plans.length}
        <section class="section">
          <h2>Membership Gift Cards</h2>
          <p>
            Each gift card is enough to pay for a membership for the time you
            choose. Your recipient adds it to their account, picks the
            membership, and the gift card pays for it.
          </p>
          <div class="cards">
            {#each groups.plans as plan}
              <div class="card" class:popular={plan.popular}>
                {#if plan.popular}<span class="badge">Most popular</span>{/if}
                <h3>{plan.name}</h3>
                <ul class="steps">
                  {#each plan.steps as step}
                    {#if step === plan.highlight}
                      <li class="highlight">
                        <svg class="icon"><use href="/icons.svg#info" /></svg>
                        {step}
                      </li>
                    {:else}
                      <li>
                        <svg class="icon"><use href="/icons.svg#check" /></svg>
                        {step}
                      </li>
                    {/if}
                  {/each}
                </ul>
                <div class="options">
                  {#each plan.options as o}
                    <div>
                      <div class="tile">
                        <span class="tile-stripe">
                          {o.label}
                          <svg class="icon"><use href="/icons.svg#gift" /></svg>
                        </span>
                        <span class="tile-top">Gift card</span>
                        <span class="tile-amount">
                          {formatPrice(o.product.price, true)}
                        </span>
                      </div>
                      <GiftCardBuyButton product={o.product} />
                    </div>
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        </section>
      {/if}

      {#if groups.packs.length}
        <section class="section">
          <h2>Puzzle Pack Gift Cards</h2>
          <p>Enough to pay for a one-time puzzle rental, with no membership.</p>
          <div class="cards">
            {#each groups.packs as c}
              <div class="card">
                <h3>{c.name}</h3>
                <ul class="steps">
                  {#each c.steps as step}
                    <li>
                      <svg class="icon"><use href="/icons.svg#check" /></svg>
                      {step}
                    </li>
                  {/each}
                </ul>
                <div class="tile">
                  <span class="tile-stripe">
                    {c.name}
                    <svg class="icon"><use href="/icons.svg#gift" /></svg>
                  </span>
                  <span class="tile-top">Gift card</span>
                  <span class="tile-amount">
                    {formatPrice(c.product.price, true)}
                  </span>
                </div>
                <GiftCardBuyButton product={c.product} />
              </div>
            {/each}
          </div>
        </section>
      {/if}

      {#if groups.other.length}
        <section class="section">
          <h2>Other Amounts</h2>
          <p>
            Can be used on anything: memberships, Puzzle Packs, or puzzles to
            keep.
          </p>
          <div class="cards">
            {#each groups.other as o}
              <div class="card">
                <div class="tile">
                  <span class="tile-stripe">
                    {isPlainAmount(o.product.title)
                      ? 'Use on anything'
                      : o.product.title}
                    <svg class="icon"><use href="/icons.svg#gift" /></svg>
                  </span>
                  <span class="tile-top">Gift card</span>
                  <span class="tile-amount">
                    {formatPrice(o.product.price, true)}
                  </span>
                </div>
                <GiftCardBuyButton product={o.product} />
              </div>
            {/each}
          </div>
        </section>
      {/if}
    {/if}

    <p class="help">
      Received a gift card? <a href="/redeem">Redeem it here</a>.
      <br />
      <a href="/gift-card/balance">Check a gift card balance</a>
    </p>
  </div>
</div>

{#if giftCardsInCart > 0}
  <div class="checkout-bar" role="status">
    <span>
      {giftCardsInCart} gift card{giftCardsInCart > 1 ? 's' : ''} in your cart
    </span>
    <a class="btn" href="/checkout">Go to Checkout</a>
  </div>
{/if}
